import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, MapPin, Building2, Compass, Sparkles, ArrowRight, MessageCircle } from "lucide-react";
import CTABand from "@/components/CTABand";
import { Reveal, easeLux } from "@/lib/motion";
import api from "@/lib/api";
import { BRAND } from "@/lib/content";

const waLink = `https://wa.me/${BRAND.whatsapp}?text=${encodeURIComponent("Hi Komel, I read your case study and would like a consultation.")}`;

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setProject(null);
    setNotFound(false);
    api.get(`/projects/${id}`).then((r) => setProject(r.data)).catch(() => setNotFound(true));
  }, [id]);

  useEffect(() => {
    if (!project) return;
    api.get(`/projects?category=${encodeURIComponent(project.category)}`).then((r) => {
      setRelated(r.data.filter((p) => p.id !== project.id).slice(0, 3));
    });
  }, [project]);

  if (notFound) {
    return (
      <div className="relative z-10 min-h-[70vh] grid place-items-center px-6">
        <div className="text-center">
          <h1 className="font-display text-4xl mb-4">Case study not found</h1>
          <Link to="/projects" className="text-gold link-underline">Back to all projects</Link>
        </div>
      </div>
    );
  }

  if (!project) return <div className="min-h-[70vh]" />;

  const chapters = [
    { n: "01", t: "Client Concern", d: project.client_concern || project.challenge, icon: Building2 },
    { n: "02", t: "What I Found", d: project.what_i_found || project.solution, icon: Compass },
    { n: "03", t: "Solution Provided", d: project.solution_provided || project.results, icon: Compass },
    { n: "04", t: "Outcome", d: project.outcome, icon: Sparkles },
  ].filter((c) => c.d);

  return (
    <div className="relative z-10" data-testid="project-detail">
      {/* Hero */}
      <header className="relative pt-32 md:pt-40 pb-16 md:pb-24 overflow-hidden">
        <motion.div initial={{ scale: 1.1, opacity: 0 }} animate={{ scale: 1, opacity: 0.35 }} transition={{ duration: 1.5, ease: easeLux }}
          className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${project.image})` }} />
        <div className="absolute inset-0 bg-gradient-to-b from-[#060A17]/70 via-[#060A17]/85 to-[#060A17]" />
        <div className="relative mx-auto max-w-4xl px-6 md:px-8">
          <button onClick={() => navigate("/projects")} className="inline-flex items-center gap-2 text-[#94A3B8] hover:text-gold transition-colors mb-8" data-testid="project-back">
            <ArrowLeft size={16} /> Back to Case Studies
          </button>
          <div className="flex flex-wrap items-center gap-3 mb-5">
            <span className="rounded-full bg-primary-green/90 px-3 py-1 text-xs text-white">{project.category}</span>
            {project.subtype && <span className="rounded-full border border-white/15 px-3 py-1 text-xs text-white/80">{project.subtype}</span>}
          </div>
          <h1 className="font-display text-4xl md:text-6xl leading-[0.98] tracking-tight text-white">{project.title}</h1>
          <p className="flex items-center gap-2 mt-5 text-[#94A3B8] text-base"><MapPin size={16} className="text-gold" /> {project.location}</p>
        </div>
      </header>

      {/* Hero image band */}
      <section className="mx-auto max-w-5xl px-6 md:px-8 -mt-6 mb-16">
        <Reveal y={30}>
          <div className="relative overflow-hidden rounded-3xl border border-white/10 shadow-[0_30px_80px_rgba(0,0,0,0.5)]">
            <img src={project.image} alt={project.title} className="w-full h-[380px] md:h-[520px] object-cover" />
            <div className="absolute top-4 left-4 rounded-full bg-black/50 backdrop-blur px-3 py-1 text-xs text-white">Case Study · {new Date(project.created_at).toLocaleDateString("en-GB", { month: "short", year: "numeric" })}</div>
          </div>
        </Reveal>
      </section>

      {/* Story chapters */}
      <section className="py-6 md:py-12">
        <div className="mx-auto max-w-3xl px-6 md:px-8 space-y-14 md:space-y-20">
          {chapters.map((c) => (
            <Reveal key={c.n}>
              <article data-testid={`chapter-${c.n}`}>
                <div className="flex items-center gap-4 mb-5">
                  <span className="font-display text-5xl text-gold/70">{c.n}</span>
                  <span className="grid place-items-center h-11 w-11 rounded-xl bg-primary-green/15 text-primary-green ring-1 ring-primary-green/30"><c.icon size={19} /></span>
                  <h2 className="font-display text-3xl md:text-4xl">{c.t}</h2>
                </div>
                <p className="text-[#CBD5E1] text-lg leading-[1.85]">{c.d}</p>
              </article>
            </Reveal>
          ))}

          <Reveal>
            <div className="rounded-2xl border border-gold/25 bg-gold/[0.05] p-8 flex flex-col md:flex-row md:items-center gap-6">
              <div className="flex-1">
                <p className="overline text-gold mb-2">Considering a similar transformation?</p>
                <h3 className="font-display text-2xl md:text-3xl">Speak with Komel directly</h3>
                <p className="text-[#94A3B8] mt-2">Every space carries its own energy signature. Let's decode yours.</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/contact" className="inline-flex items-center justify-center gap-2 rounded-full bg-primary-green px-6 py-3 font-medium text-white hover:bg-[#059669] transition-colors" data-testid="project-cta-book">
                  Book Consultation <ArrowRight size={16} />
                </Link>
                <a href={waLink} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-6 py-3 font-medium text-white transition-transform hover:-translate-y-0.5">
                  <MessageCircle size={16} /> WhatsApp
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="py-16 md:py-24 bg-[#14100B]">
          <div className="mx-auto max-w-7xl px-6 md:px-8">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
              <Reveal>
                <p className="overline text-gold mb-4">More from {project.category}</p>
                <h2 className="font-display text-3xl md:text-4xl">Related case studies</h2>
              </Reveal>
              <Reveal delay={0.1}>
                <Link to="/projects" className="inline-flex items-center gap-2 text-gold link-underline">All projects <ArrowRight size={15} /></Link>
              </Reveal>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((r, i) => (
                <Reveal key={r.id} delay={i * 0.08}>
                  <Link to={`/projects/${r.id}`} className="group block overflow-hidden rounded-3xl border border-white/10 transition-all duration-500 hover:border-gold/30" data-testid={`related-project-${i}`}>
                    <div className="relative h-52 overflow-hidden">
                      <img src={r.image} alt={r.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#060A17] to-transparent" />
                      <span className="absolute top-4 left-4 rounded-full bg-primary-green/90 px-3 py-1 text-xs text-white">{r.category}</span>
                    </div>
                    <div className="p-6">
                      <h3 className="font-display text-xl group-hover:text-gold transition-colors">{r.title}</h3>
                      <p className="text-[#94A3B8] text-sm mt-1 flex items-center gap-1.5"><MapPin size={13} /> {r.location}</p>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}
      <CTABand />
    </div>
  );
}
